import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";

/**
 * Two-button language toggle rendered in the eyebrow of the public talent
 * fiche. Keeps the URL in sync with the active language so the visitor can
 * share the page in their picked language and so the CSM "Copier (FR/EN)"
 * buttons produce a working URL.
 */
interface Props {
	language: "fr" | "en";
	onChange: (lang: "fr" | "en") => void;
}

export const PublicLanguageSwitcher: React.FC<Props> = ({
	language,
	onChange
}) => {
	const { t } = useTranslation();
	return (
		<Box
			role="group"
			aria-label={t("anonymizedTalent.languageSwitcherAria")}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0,
				border: "1px solid rgba(255,255,255,0.25)",
				borderRadius: 100,
				overflow: "hidden",
				fontSize: 11
			}}
		>
			<LangButton
				active={language === "fr"}
				onClick={() => onChange("fr")}
				label="FR"
			/>
			<LangButton
				active={language === "en"}
				onClick={() => onChange("en")}
				label="EN"
			/>
		</Box>
	);
};

const LangButton: React.FC<{
	active: boolean;
	onClick: () => void;
	label: string;
}> = ({ active, onClick, label }) => (
	<Box
		component="button"
		onClick={onClick}
		type="button"
		// aria-pressed marks this as a toggle so screen readers announce the
		// state correctly ("FR, pressed" / "EN, not pressed").
		aria-pressed={active}
		aria-label={label}
		sx={{
			border: "none",
			background: active ? zoullooColors.brand.coquelicot : "transparent",
			color: active ? "white" : "rgba(255,255,255,0.85)",
			fontWeight: 700,
			fontSize: 11,
			letterSpacing: "0.5px",
			px: 1.5,
			py: 0.5,
			cursor: "pointer",
			fontFamily: "inherit",
			transition: "background 120ms",
			"&:hover": {
				background: active
					? zoullooColors.brand.coquelicot
					: "rgba(255,255,255,0.08)"
			}
		}}
	>
		{label}
	</Box>
);

export default PublicLanguageSwitcher;
